import { useSelector } from "react-redux";
import { useUsers } from "../../../../modules/users/hooks/useUsers";
import {
  selectModalUser,
  selectIsUpdateUser,
  selectUserForUpdate,
} from "../../../../redux";
import { WrapperDrawerUsers } from "./styled";
import { useFormContext, useWatch } from "react-hook-form";
import { IUserResFind } from "../../../../redux/@types";
import { Button, Col, Typography } from "antd";
import { InputNumber, InputPassword, InputSelect, InputText } from "../inputs";
import { AdminModulesSelector } from "../AdminModulesSelector";
import { storesService } from "../../../services";
import React from "react";

const { Title } = Typography;

const dataOptionsRole = [
  { value: "user", title: "Usuario" },
  { value: "seller", title: "Vendedor" },
  { value: "admin", title: "Administrador" },
  { value: "domiciliary", title: "Domiciliario" },
];

const dataOptionsStatus = [
  { value: "active", title: "Activo" },
  { value: "inactive", title: "Inactivo" },
  {
    value: "pending security verification",
    title: "Verificación de seguridad pendiente",
  },
  { value: "pending user data", title: "Datos de usuario pendientes" },
  { value: "pending validation", title: "Validación pendiente" },
];

export type RoleSelectOption = { value: string; title: string };

export const ModalUsers = ({
  roleSelectOptions,
  showModulePermissions = false,
}: {
  roleSelectOptions?: RoleSelectOption[];
  /** Usuarios del sistema (admin / vendedor): elegir módulos visibles. */
  showModulePermissions?: boolean;
}) => {
  const modalUser = useSelector(selectModalUser);
  const isUpdateUser = useSelector(selectIsUpdateUser);
  const updateUser = useSelector(selectUserForUpdate);
  const { isLoading, changeModalUserState, onSubmitCreateOrUpdate } =
    useUsers();

  const { control, handleSubmit: onSubmit } = useFormContext<IUserResFind>();

  const role = useWatch({ control, name: "role" });
  const roleOptions = roleSelectOptions ?? dataOptionsRole;
  const isDomiciliary = role === "domiciliary";
  const canAssignModules =
    showModulePermissions && (role === "admin" || role === "seller");

  const [storeOptions, setStoreOptions] = React.useState<
    { value: number; title: string }[]
  >([]);

  React.useEffect(() => {
    if (!modalUser || !isDomiciliary) return;
    storesService
      .find({ query: { $limit: 500, $sort: { name: 1 } } })
      .then((res) => {
        const rows = "data" in res ? res.data : [];
        const list = Array.isArray(rows) ? rows : [];
        setStoreOptions(
          list.map((s: { id: number; name: string; address?: string }) => ({
            value: s.id,
            title: s.address ? `${s.name} — ${s.address}` : s.name,
          }))
        );
      })
      .catch(() => setStoreOptions([]));
  }, [modalUser, isDomiciliary]);

  const drawerTitle = isUpdateUser
    ? `Editar usuario #${updateUser?.id}`
    : roleSelectOptions?.length === 1 &&
        roleSelectOptions[0]?.value === "domiciliary"
      ? "Crear domiciliario"
      : "Crear usuario";

  const submitLabel = isUpdateUser
    ? "Guardar cambios"
    : roleSelectOptions?.length === 1 &&
        roleSelectOptions[0]?.value === "domiciliary"
      ? "Crear domiciliario"
      : "Crear usuario";

  return (
    <WrapperDrawerUsers
      title={drawerTitle}
      placement="right"
      width={canAssignModules ? 480 : 440}
      open={modalUser}
      onClose={changeModalUserState}
      destroyOnClose
    >
      <Col span={24} style={{ marginTop: 4 }}>
        <Title style={{ marginBottom: "4px" }} level={5}>
          Nombre
        </Title>
        <InputText
          placeHolder="Juan David"
          name="first_name"
          control={control}
        />
      </Col>
      <Col span={24} style={{ marginTop: 12 }}>
        <Title style={{ marginBottom: "4px" }} level={5}>
          Apellidos
        </Title>
        <InputText
          placeHolder="Velázquez Molina"
          name="last_name"
          control={control}
        />
      </Col>
      <Col span={24} style={{ marginTop: 12 }}>
        <Title style={{ marginBottom: "4px" }} level={5}>
          Cédula
        </Title>
        <InputNumber
          stringMode={true}
          placeHolder="123456789"
          name="dni"
          control={control}
        />
      </Col>
      <Col span={24} style={{ marginTop: 12 }}>
        <Title style={{ marginBottom: "4px" }} level={5}>
          Correo
        </Title>
        <InputText
          placeHolder="example@example.com"
          name="email"
          control={control}
        />
      </Col>
      <Col span={24} style={{ marginTop: 12 }}>
        <Title style={{ marginBottom: "4px" }} level={5}>
          Contraseña
        </Title>
        <InputPassword name="password" control={control} />
      </Col>
      <Col span={24} style={{ marginTop: 12 }}>
        <Title style={{ marginBottom: "4px" }} level={5}>
          Teléfono
        </Title>
        <InputNumber
          stringMode={true}
          placeHolder="3292321143"
          name="phone"
          control={control}
        />
      </Col>
      <Col span={24} style={{ marginTop: 12 }}>
        <InputSelect
          name="role"
          control={control}
          dataOptions={roleOptions}
          label="Rol"
          placeholder="Seleccione rol"
        />
      </Col>
      {isDomiciliary && (
        <Col span={24} style={{ marginTop: 12 }}>
          <InputSelect
            name="store_id"
            control={control}
            dataOptions={storeOptions}
            label="Sede asignada"
            placeholder="Seleccione la sede"
            showSearch
          />
        </Col>
      )}
      <Col span={24} style={{ marginTop: 12 }}>
        <InputSelect
          name="status"
          control={control}
          dataOptions={dataOptionsStatus}
          label="Estado"
          placeholder="Estado"
        />
      </Col>
      {canAssignModules && (
        <AdminModulesSelector control={control} />
      )}
      <Button
        style={{
          width: "100%",
          marginTop: 20,
        }}
        type="primary"
        shape="round"
        size="large"
        onClick={onSubmit(onSubmitCreateOrUpdate)}
        loading={isLoading}
      >
        {submitLabel}
      </Button>
    </WrapperDrawerUsers>
  );
};
